/**
 * Shade-to-main-image swap for the v2 PDP (`main-product-v2`).
 *
 * De-branded duplicate of `shade-media-swap.js` (`<aiw-shade-media-swap>`), which is
 * scoped to the AIW PDP and deliberately left untouched.
 *
 * Two modes, driven by data-shade-image-mode on <concealer-variant>:
 * - "per_shade" (default): reads the shade-name → URL map from data-shade-images,
 *   stamps data-shade-image onto each radio, and swaps the overlay src on hover/change.
 * - "single": reads data-shade-image-single and pre-sets the overlay <img> src once.
 *   Hover/change just toggles overlay visibility.
 *
 * Thumbnail click clears the overlay in both modes.
 */
if (customElements.get('shade-media-swap') === undefined) {
  class ShadeMediaSwap extends HTMLElement {
    constructor() {
      super();
      this.onChange = this.onChange.bind(this);
      this.onLabelMouseEnter = this.onLabelMouseEnter.bind(this);
      this.onThumbnailClick = this.onThumbnailClick.bind(this);
    }

    connectedCallback() {
      this.root = this.closest('.main-product-v2');
      if (!this.root) return;

      this.gallery = this.root.querySelector('media-gallery');
      if (!this.gallery) return;

      this.concealerVariant = this.root.querySelector('concealer-variant');
      if (!this.concealerVariant) return;

      this.shadeImageMode = this.concealerVariant.dataset.shadeImageMode || 'per_shade';
      this.singleShadeImage = this.concealerVariant.dataset.shadeImageSingle || '';

      if (this.shadeImageMode !== 'single') {
        this.shadeMap = {};
        try {
          this.shadeMap = JSON.parse(this.concealerVariant.dataset.shadeImages || '{}');
        } catch (e) {
          // Malformed setting breaks shade-to-image swap entirely — promote to error
          // so the silent UX failure (clicking a shade does nothing) is visible in logs.
          if (typeof console !== 'undefined') console.error('[shade-media-swap] shade-images setting is malformed JSON — shade swap disabled:', e, this.concealerVariant.dataset.shadeImages);
        }

        this.root.querySelectorAll('.swatch-input__input, .concealer-variant__shade-radio').forEach((radio) => {
          const title = radio.dataset.shadeTitle || radio.value;
          if (title && this.shadeMap[title]) {
            radio.dataset.shadeImage = this.shadeMap[title];
          }
        });
      }

      this.viewer = this.gallery.querySelector('.slider-mobile-gutter');
      if (!this.viewer) return;

      this.buildOverlay();

      // Single mode: set the overlay <img> src once so hover/change just toggles visibility.
      if (this.shadeImageMode === 'single' && this.singleShadeImage) {
        this.overlayImg.src = this.singleShadeImage;
      }

      this.root.addEventListener('change', this.onChange);

      this.labels = this.root.querySelectorAll('.swatch-input__label, .concealer-variant__shade-label');
      this.labels.forEach((label) => label.addEventListener('mouseenter', this.onLabelMouseEnter));

      this.thumbnails = this.gallery.querySelector('.thumbnail-slider');
      if (this.thumbnails) this.thumbnails.addEventListener('click', this.onThumbnailClick);
    }

    disconnectedCallback() {
      if (this.root) this.root.removeEventListener('change', this.onChange);
      if (this.labels) this.labels.forEach((label) => label.removeEventListener('mouseenter', this.onLabelMouseEnter));
      if (this.thumbnails) this.thumbnails.removeEventListener('click', this.onThumbnailClick);
      if (this.overlay && this.overlay.parentElement) this.overlay.parentElement.removeChild(this.overlay);
    }

    buildOverlay() {
      this.overlay = document.createElement('div');
      this.overlay.className = 'shade-image-overlay';
      this.overlay.style.cssText = [
        'position: absolute',
        'inset: 0',
        'display: none',
        'background: #fff',
        'z-index: 3',
        'pointer-events: none',
      ].join(';');

      this.shadow = document.createElement('div');
      this.shadow.className = 'shade-image-overlay__shadow';
      this.shadow.style.cssText = [
        'position: absolute',
        'inset: 0',
        'background: rgba(0, 0, 0, 0.45)',
        'opacity: 0',
        'transition: opacity 0.2s ease',
        'z-index: 2',
        'pointer-events: none',
      ].join(';');

      this.spinner = document.createElement('div');
      this.spinner.className = 'shade-image-overlay__spinner';
      this.spinner.style.cssText = [
        'position: absolute',
        'top: 50%',
        'left: 50%',
        'transform: translate(-50%, -50%)',
        'width: 4rem',
        'height: 4rem',
        'opacity: 0',
        'transition: opacity 0.2s ease',
        'z-index: 3',
        'pointer-events: none',
      ].join(';');
      this.spinner.innerHTML =
        '<svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg" style="width:100%;height:100%;">' +
        '<circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30" style="stroke: #fff;"></circle>' +
        '</svg>';

      this.overlayImg = document.createElement('img');
      this.overlayImg.alt = '';
      this.overlayImg.style.cssText = 'width:100%;height:100%;object-fit:contain;display:block;';

      this.overlay.appendChild(this.overlayImg);
      this.overlay.appendChild(this.shadow);
      this.overlay.appendChild(this.spinner);

      const cs = window.getComputedStyle(this.viewer);
      if (cs.position === 'static') this.viewer.style.position = 'relative';
      this.viewer.appendChild(this.overlay);
    }

    setLoading(isLoading) {
      this.shadow.style.opacity = isLoading ? '1' : '0';
      this.spinner.style.opacity = isLoading ? '1' : '0';
    }

    showShadeImage(url) {
      if (!url) return;
      this.overlay.style.display = 'block';
      if (this.overlayImg.src === url && this.overlayImg.complete) return;
      this.setLoading(true);
      this.overlayImg.onload = () => this.setLoading(false);
      this.overlayImg.onerror = () => this.setLoading(false);
      this.overlayImg.src = url;
    }

    hideShadeImage() {
      this.overlay.style.display = 'none';
      this.setLoading(false);
    }

    onChange(e) {
      const radio = e.target;
      if (!radio || radio.type !== 'radio' || !radio.checked) return;

      const isShadeRadio =
        radio.classList.contains('concealer-variant__shade-radio') ||
        radio.classList.contains('swatch-input__input');
      if (!isShadeRadio) return;

      if (this.shadeImageMode === 'single') {
        if (this.singleShadeImage) this.overlay.style.display = 'block';
        else this.hideShadeImage();
        return;
      }

      const url = radio.dataset.shadeImage;
      if (url) this.showShadeImage(url);
      else this.hideShadeImage();
    }

    onLabelMouseEnter(e) {
      if (this.shadeImageMode === 'single') {
        if (this.singleShadeImage) this.overlay.style.display = 'block';
        return;
      }
      const label = e.currentTarget;
      const radio = label.previousElementSibling || label.parentElement.querySelector('input[type="radio"]');
      if (!radio) return;
      const url = radio.dataset.shadeImage;
      if (url) this.showShadeImage(url);
    }

    onThumbnailClick(e) {
      if (e.target.closest('.thumbnail-list__item')) this.hideShadeImage();
    }
  }

  customElements.define('shade-media-swap', ShadeMediaSwap);
}
